import { validateTaiwanPhone } from "@components/form/validate/vaildateTaiwanPhone";
import RadiusBoxLabel from "@components/layout/RadiusBoxLabel";
import RadiusCard from "@components/layout/RadiusCard";
import appToast from "@components/Toast";
import { zodResolver } from "@hookform/resolvers/zod";
import liff from "@line/liff";
import api, { handleRequest } from "@services/axios/apiManager";
import { colors } from "@styles/colors";
import { containerStyle } from "@styles/index";
import { getSendOtpErrorMessage, getVerifyOtpErrorMessage } from "@utils/otpError";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { GasData, useUser } from "@utils/store/user/useUser";
import { useUtilsStore } from "@utils/store/utils/useUtilsTool";
import { AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { RiPhoneLine, RiShieldCheckLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { Button, Form, Input, Text, XStack, YStack } from "tamagui";
import { z } from "zod";

import { OTPInputBlocks } from "@components/form/OTPInputBlocks";
import { track } from "@lib/tracker";

import { maskPhone } from "@utils/func/maskPhone";
import { VOICE_OTP_CALLER_ID } from "@utils/func/voiceOtp";

const VerifyPhone = () => {
  const { lineAccessToken } = useLineAuth();
  const { setLoading } = useUtilsStore();
  const { setInvGasData, invGasData } = useUser();
  const navigate = useNavigate();
  const [sendState, setSendState] = useState<any>(null);
  const [countdown, setCountdown] = useState(0);
  // 語音倒數與簡訊分開：後端兩個管道各自計冷卻（簡訊 60s / 語音 90s），
  // 前端若共用一個倒數，簡訊冷卻中就會把語音按鈕一起鎖住——而那正是使用者
  // 收不到簡訊、最需要改用語音的時刻。
  const [voiceCountdown, setVoiceCountdown] = useState(0);
  // 最後一次是用哪個管道送出的，只影響畫面文案（簡訊「已傳送至」/ 語音「已撥打至」）。
  const [lastChannel, setLastChannel] = useState<"sms" | "voice">("sms");

  //@ 判斷是否有 invGasData (如沒有表示沒有走客戶序號流程, 直接導回第一步)
  useEffect(() => {
    if (!invGasData) {
      setLoading(true);
      const t = setTimeout(() => {
        setLoading(false); // 導頁前先關全域 spinner
        navigate("/register/step2/inviteCode");
      }, 500);
      // 卸載（按返回離開本頁）時務必清掉 timer + 全域 loading，否則 spinner 會卡死
      // （invGasData 於返回時 sessionStorage 補水前偶為 null，會踩到此分支 → 一直 loading）。
      return () => {
        clearTimeout(t);
        setLoading(false);
      };
    }
  }, [invGasData]);

  //@ 倒數計時邏輯（簡訊）
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  //@ 倒數計時邏輯（語音）
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (voiceCountdown > 0) {
      timer = setInterval(() => {
        setVoiceCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [voiceCountdown]);

  //@ register 漏斗：進入註冊流程（手機驗證頁載入 = 開始註冊）
  useEffect(() => {
    track("funnel_step", { funnel: "register", step: "register_enter" });
  }, []);

  //@ 建立 Form
  const formSchema = z.object({
    phone: z.string().min(1, { message: "請輸入有效的手機號碼 (09開頭共10碼) 或市話 (含區碼)" }).refine(validateTaiwanPhone, {
      message: "請輸入有效的手機號碼 (09開頭共10碼) 或市話 (含區碼)",
    }),
    otp: z.string().optional(),
  });

  type FormData = z.infer<typeof formSchema>;
  const {
    control,
    handleSubmit,
    setError,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phone: "",
      otp: "",
    },
  });

  //@ 發送驗證碼
  const onSubmit = async (data: FormData) => {
    if (lineAccessToken) {
      track("funnel_step", { funnel: "register", step: "otp_send" });
      handleRequest({
        asyncRequest: () =>
          api.post(
            "/app-api/line/send-phone-otp",
            {
              accessToken: lineAccessToken,
              phone: data?.phone || "",
            },
            { isAuth: false },
          ),
        successCallback: (res: AxiosResponse) => {
          setSendState(res.data.data);
          // 冷卻秒數以後端為準（簡訊 60s），不要前端自己寫死——兩邊對不起來時
          // 使用者會看到倒數歸零卻仍被 429 擋下。
          setCountdown(res?.data?.data?.resendAvailableInSeconds ?? 60);
          setLastChannel("sms");
          setValue("otp", ""); // 清空舊的 OTP
          appToast.success("驗證碼已發送");
        },
        errorCallback: (error: any) => {
          setError("phone", {
            type: "manual",
            message: getSendOtpErrorMessage(error),
          });
        },
        finalCallback: () => {},
        setLoading,
      });
    } else {
      liff.login();
    }
  };

  //@ 改用語音：同一組驗證碼改以電話播報（TwSMS Voice），驗證走同一支 verify-phone-otp。
  //  存在理由：市話收不到簡訊，且企業簡訊常被電信端當廣告攔截。
  const onSendVoiceOtp = () => {
    if (!lineAccessToken) {
      liff.login();
      return;
    }
    track("funnel_step", { funnel: "register", step: "otp_send_voice" });
    handleRequest({
      asyncRequest: () =>
        api.post(
          "/app-api/line/send-phone-otp",
          {
            accessToken: lineAccessToken,
            phone: getValues("phone") || "",
            channel: "voice",
          },
          { isAuth: false },
        ),
      successCallback: (res: AxiosResponse) => {
        setSendState(res.data.data);
        setVoiceCountdown(res?.data?.data?.resendAvailableInSeconds ?? 90);
        setLastChannel("voice");
        setValue("otp", "");
        appToast.success(`電話將於 30 秒內撥入（${VOICE_OTP_CALLER_ID}）`);
      },
      errorCallback: (error: any) => {
        // 此時手機號碼欄位已不在畫面上（畫面切到 OTP 區），錯誤只能用 toast 呈現，
        // 掛在 phone 欄位上使用者看不到。
        appToast.error(getSendOtpErrorMessage(error));
      },
      finalCallback: () => {},
      setLoading,
    });
  };

  //@ 驗證 OTP
  const onVerifyOTP = async (otpValue: string) => {
    if (!otpValue || otpValue.length !== 5) {
      setError("otp", { type: "manual", message: "請輸入 5 位數驗證碼" });
      return;
    }

    const phone = getValues("phone");

    if (lineAccessToken) {
      track("funnel_step", { funnel: "register", step: "otp_submit" });
      handleRequest({
        asyncRequest: () =>
          api.post(
            "/app-api/line/verify-phone-otp",
            {
              accessToken: lineAccessToken,
              phone: phone,
              otpCode: otpValue,
            },
            { isAuth: false },
          ),
        successCallback: (res: AxiosResponse) => {
          setInvGasData({ ...invGasData, verifiedPhone: phone, phoneVerifiedTicket: res?.data?.data?.phoneVerifiedTicket || "" } as GasData);
          // register 漏斗：手機 OTP 驗證成功
          track("funnel_step", { funnel: "register", step: "otp_verified" });
          appToast.success("驗證成功");
          navigate("/register/profile");
        },
        errorCallback: (error: any) => {
          setError("otp", {
            type: "manual",
            message: getVerifyOtpErrorMessage(error),
          });
        },
        setLoading,
      });
    }
  };

  return (
    <>
      <YStack {...containerStyle}>
        {!sendState?.success ? (
          <Form onSubmit={handleSubmit(onSubmit)}>
            <YStack gap={15}>
              <RadiusCard>
                <RadiusBoxLabel label="手機號碼（🇹🇼+886）" icon={<RiPhoneLine color={colors.primary} />} />
                <YStack mt="$3">
                  <Text fontSize={14} fontWeight="bold" color={colors.gray} mb={4}>
                    請輸入您的手機號碼
                  </Text>
                  <Controller
                    control={control}
                    name="phone"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <Input
                        fontSize={"$4"}
                        placeholder="輸入手機號碼"
                        keyboardType="phone-pad"
                        textAlign={"center"}
                        value={value}
                        onBlur={onBlur}
                        letterSpacing={3}
                        borderColor={errors.phone ? "$red10" : undefined}
                        borderWidth={errors.phone ? 1 : 0}
                        onChangeText={(text: string) => {
                          onChange(text);
                        }}
                      />
                    )}
                  />
                  {errors.phone && (
                    <Text color="$red10" fontSize={14} mt="$2">
                      {errors.phone.message}
                    </Text>
                  )}
                </YStack>
              </RadiusCard>
              <RadiusCard bg="rgba(255, 255, 255, 0.3)" border="#e2e8f0" borderRadius={10}>
                <Text fontSize={14} color={colors.gray}>
                  🔒 點擊「取得驗證碼」即表示您同意 服務條款 與 隱私政策
                </Text>
              </RadiusCard>
            </YStack>
            <XStack
              className="primary-bg-gradient"
              borderRadius={20}
              p={15}
              mt={15}
              alignItems="center"
              justifyContent="center"
              onPress={handleSubmit(onSubmit)}
            >
              <XStack gap={10}>
                <Text color="white" fontSize={16} fontWeight="bold">
                  取得驗證碼
                </Text>
              </XStack>
            </XStack>
          </Form>
        ) : (
          <YStack gap={15}>
            <RadiusCard>
              <RadiusBoxLabel label="驗證碼" icon={<RiShieldCheckLine color={colors.primary} />} />
              <YStack mt="$3" alignItems="center">
                <Text fontSize={14} fontWeight="bold" color={colors.gray} alignSelf="flex-start">
                  請輸入 5 位數驗證碼
                </Text>
                <Text fontSize={14} color={colors.gray} my="$3" fontWeight="bold">
                  {lastChannel === "voice" ? "已撥打電話至" : "已傳送至"} {maskPhone(getValues("phone"))}
                </Text>
                <Controller
                  control={control}
                  name="otp"
                  render={({ field: { onChange, value } }) => (
                    <OTPInputBlocks value={value || ""} onChange={onChange} onComplete={(val) => onVerifyOTP(val)} error={errors.otp?.message} />
                  )}
                />
                {errors.otp && (
                  <Text color="$red10" fontSize={14} mt="$2" alignSelf="center">
                    {errors.otp.message}
                  </Text>
                )}

                <YStack mt="$2" alignItems="center" gap="$1">
                  <Button
                    chromeless
                    size="$3"
                    color={countdown > 0 ? colors.gray : colors.primary}
                    disabled={countdown > 0}
                    onPress={() => onSubmit(getValues())}
                  >
                    {countdown > 0 ? `重新傳送驗證碼 (${countdown}s)` : "重新傳送驗證碼"}
                  </Button>
                  {/* 收不到簡訊的救援管道：改由系統打電話唸驗證碼（市話也收得到）。
                      與「重新傳送」同為藍字，但不加粗、字級小一級，並以上方細線分開——
                      它是備援出口，不該和主要動作看起來像兩個同等選項。
                      倒數與簡訊分開，簡訊冷卻中仍可立即改用語音。 */}
                  <XStack height={1} backgroundColor="#F1F5F9" width="55%" my="$2" />
                  <Button
                    chromeless
                    size="$2"
                    color={voiceCountdown > 0 ? "#CBD5E1" : colors.primary}
                    fontWeight="400"
                    disabled={voiceCountdown > 0}
                    onPress={onSendVoiceOtp}
                  >
                    {voiceCountdown > 0 ? `語音驗證 ${voiceCountdown} 秒後可再試` : "改用語音驗證"}
                  </Button>
                </YStack>
              </YStack>
            </RadiusCard>
            <Button
              chromeless
              size="$3"
              color={colors.primary}
              onPress={() => {
                setSendState(null);
                setValue("otp", "");
              }}
            >
              重新輸入手機號碼
            </Button>
            <RadiusCard bg="rgba(255, 255, 255, 0.3)" border="#e2e8f0" borderRadius={10}>
              <Text fontSize={14} color={colors.gray}>
                {lastChannel === "voice"
                  ? `📞 系統將以 ${VOICE_OTP_CALLER_ID} 撥打給您，電話中會唸兩次驗證碼`
                  : "📱 簡訊通常在 30 秒內送達,請稍候。收不到可改用語音驗證"}
              </Text>
            </RadiusCard>
          </YStack>
        )}
      </YStack>
    </>
  );
};

export default VerifyPhone;
